import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, Eye, EyeSlash, Spinner } from '@phosphor-icons/react';

export function LoginPage() {
  const { login, error } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    setLoading(true);
    
    const result = await login(email, password);
    setLoading(false);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setLocalError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0E14] flex">
      {/* Left Panel - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1669887961943-54dd571fb287?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxub3J3YXklMjBmam9yZCUyMG5pZ2h0JTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzQ3NTM0NDF8MA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0E14] via-[#0B0E14]/80 to-transparent" />
        <div className="relative z-10 flex flex-col justify-center p-12">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#E2B05E] to-[#F0C47A] flex items-center justify-center">
              <Shield weight="fill" className="w-10 h-10 text-[#0B0E14]" />
            </div>
            <div>
              <h1 className="font-['Outfit'] text-4xl font-bold text-[#F3F4F6] tracking-tight">VALHALLA</h1>
              <p className="text-[#9CA3AF] tracking-[0.2em] uppercase text-sm">Corporations S.E.P.</p>
            </div>
          </div>
          <h2 className="font-['Outfit'] text-3xl font-semibold text-[#F3F4F6] mb-4">
            Sistema de Control Operativo
          </h2>
          <p className="text-[#9CA3AF] text-lg max-w-md leading-relaxed">
            Gestiona empleados, proyectos, tareas, horas, pagos y rentabilidad en un solo lugar.
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E2B05E] to-[#F0C47A] flex items-center justify-center">
              <Shield weight="fill" className="w-8 h-8 text-[#0B0E14]" />
            </div>
            <div>
              <h1 className="font-['Outfit'] text-2xl font-bold text-[#F3F4F6]">VALHALLA</h1>
              <p className="text-[10px] text-[#9CA3AF] tracking-[0.15em] uppercase">Corporations</p>
            </div>
          </div>

          <div className="valhalla-card p-8">
            <h2 className="font-['Outfit'] text-2xl font-semibold text-[#F3F4F6] mb-2">
              {t('login')}
            </h2>
            <p className="text-[#9CA3AF] mb-6">
              {t('welcomeBack')}
            </p>

            {(localError || error) && (
              <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {localError || error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#9CA3AF]">{t('email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="valhalla-input"
                  placeholder="admin@valhalla.com"
                  required
                  data-testid="login-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#9CA3AF]">{t('password')}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="valhalla-input pr-10"
                    placeholder="••••••••"
                    required
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#F3F4F6]"
                  >
                    {showPassword ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#E2B05E] hover:bg-[#F0C47A] text-[#0B0E14] font-semibold valhalla-btn"
                data-testid="login-submit"
              >
                {loading ? (
                  <Spinner className="w-5 h-5 animate-spin" />
                ) : (
                  t('login')
                )}
              </Button>
            </form>

            <p className="mt-6 text-center text-[#9CA3AF] text-sm" data-testid="login-admin-note">
              {t('contactAdminForAccess')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
